/**
 * `@floorfile/i18n/enums` — the shared vocabulary for contract enum values.
 *
 * Read `./copy.ts` for what belongs here and why this is a subpath rather than
 * a package. In short: the ten records PDFs printed raw codes (`OPEN / HIGH`)
 * while every screen printed words, and the words were already written — thirty
 * times, in `apps/web`. This is where they live now, and both the browser and
 * the Worker import them from here.
 *
 * ## The two consumers resolve it differently, as they do for every catalogue
 *
 * - **`apps/web`** hands `enumCopy` to `useCopy()` like any other catalogue,
 *   and registers `enumTranslationLoaders` through
 *   `app/locale-catalogues.ts` — the same explicit path `@floorfile/ui` takes,
 *   because `import.meta.glob` is rooted in the app's own `src` and cannot see
 *   a package.
 * - **`apps/api`** registers French EAGERLY at module scope (see
 *   `platform/i18n/index.ts`) and resolves synchronously, because a Worker has
 *   no device to save bytes on and a notification may be composed by a cron
 *   with nobody waiting.
 *
 * **French is NOT re-exported from here.** `copy.fr.ts` is reachable only as
 * `@floorfile/i18n/enums/fr` (for the Worker's eager registration) or through
 * the dynamic loader below (for the browser's one-chunk-per-locale rule, D-L6).
 * A static re-export here would put French in every bundle that wanted English.
 */
export { enumCopy, type EnumCopy } from './copy'
export type { EnumLabelsAreExhaustive } from './exhaustive'

import type { AppLocale } from '../types'

/**
 * Written once, asserted against `defineCopy('enums', …)` by
 * `apps/web/src/app/__tests__/copy-catalogues.test.ts` — the same arrangement
 * `UI_NAMESPACE` has, and for the same reason: everywhere else the namespace is
 * derived from the directory path twice and the test checks the two agree, but
 * a package catalogue has only one source.
 */
export const ENUM_NAMESPACE = 'enums'

type CatalogueLoader = () => Promise<unknown>

/**
 * Non-English catalogues, as dynamic imports.
 *
 * `manualChunks` in `apps/web/vite.config.ts` matches `**\/copy.<locale>.ts` by
 * path with no package restriction, so this joins the single `locale-fr` chunk
 * rather than becoming a request of its own. Adding a locale is one line here
 * plus a `copy.<code>.ts` beside it.
 */
export const enumTranslationLoaders: Readonly<Partial<Record<AppLocale, CatalogueLoader>>> = {
  fr: () => import('./copy.fr'),
}
